import { Suspense } from 'react';
import { Await, useLoaderData } from 'react-router-dom';
import type { AgreementDetails } from '@/api/schemas';
import { AgreementHeader } from '@/components/agreement/AgreementHeader';
import { PaymentOverviews } from '@/components/agreement/PaymentOverviews';
import { PaymentScheduleTable } from '@/components/agreement/PaymentScheduleTable';
import { PreviousPageButton } from '@/components/BackButton';
import { Badge } from '@/components/ui/badge';
import { SectionHeading } from '@/components/ui/section-heading';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AssociatedContractsTable } from '@/components/vin/AssociatedContractsTable';
import type { agreementLoader, Loaded } from '@/routes/loaders';

function AgreementBody({ agreement }: { agreement: AgreementDetails }) {
  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6 px-6 pb-8 first:pt-6">
        <AgreementHeader agreement={agreement} />

        {agreement.schedule ? (
          <>
            <PaymentOverviews schedule={agreement.schedule} />
            <PaymentScheduleTable
              agreement={agreement}
              schedule={agreement.schedule}
            />
          </>
        ) : (
          <div className="rounded-[4px] border border-notice-stroke p-4 text-[14px] intl-text-secondary">
            The payment schedule is unavailable right now. Try again later.
          </div>
        )}

        <div className="rounded-md px-4">
          <div className="flex items-center gap-2">
            <SectionHeading>Associated Contracts</SectionHeading>
            <Badge variant="count">{agreement.contracts.length}</Badge>
          </div>
          {agreement.contracts.length === 0 ? (
            <div className="pb-5 text-[13px] intl-text-secondary">
              No contracts linked to this agreement yet.
            </div>
          ) : (
            <AssociatedContractsTable
              contracts={agreement.contracts}
              lastRecordedOdometer={undefined}
              showVin
            />
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

export function AgreementPage() {
  const data = useLoaderData<typeof agreementLoader>();

  return (
    <div className="min-h-full bg-white">
      <PreviousPageButton />

      <Suspense
        fallback={
          <div className="p-8 text-center intl-text-secondary">
            Loading agreement...
          </div>
        }
      >
        <Await resolve={data.agreement}>
          {(result: Loaded<AgreementDetails>) =>
            result.ok ? (
              <AgreementBody agreement={result.value} />
            ) : (
              <div className="p-8 text-center intl-text-secondary">
                {result.message}
              </div>
            )
          }
        </Await>
      </Suspense>
    </div>
  );
}
